import { Component, OnInit, OnDestroy } from "@angular/core"
import {
  CalendarEvent,
  MonthViewDay,
} from "calendar-utils"
import { Store } from "@ngrx/store"
import { AppState } from "@store/app-state"
import { Utilities } from "@helpers/utilities"
import { Term } from "@models/term"
import { PublicHoliday } from "@models/public-holiday"
import { ConstantsStore } from "@store/constants"
import { CalendarStore } from "@store/calendar";
import { ModalService, ModalConfig } from "@services/modal"
import { CalendarDayFormComponent } from "./calendar-day-form"
import {
  POPULATE_ACTIVE_DAY,
  POPULATE_EDITABLE_TERM,
  POPULATE_EDITABLE_PUBLIC_HOLIDAY
} from "@reducers/calendar";
import * as moment from "moment";
import { TermExtraDay } from "@models/term-extra-day";
import { Subscription } from "rxjs/Subscription";

@Component({
  selector: 'calendar-view',
  templateUrl: './calendar-view.pug',
})
export class CalendarViewComponent implements OnInit, OnDestroy {

  readonly VIEW_MODE: string = "month"

  public view: string = this.VIEW_MODE
  public date: Date = new Date()
  public terms: Term[] = []
  public publicHolidays: PublicHoliday[] = []
  public events: CalendarEvent[] = []

  public constantsSubscription: Subscription
  public calendarSubscription: Subscription

  constructor(
    public store: Store<AppState>,
    public modalService: ModalService,
  ) {
  }

  ngOnInit() {
    this.constantsSubscription =
      this.store.select("constantsReducer")
        .subscribe((c: ConstantsStore) => {
          this.publicHolidays   = c.constants.publicHolidays
          this.populateEvents()
        })

    this.calendarSubscription =
      this.store.select("calendarReducer")
        .subscribe((c: CalendarStore) => {
          this.terms            = c.terms
          this.populateEvents()
        })
  }

  populateEvents() {
    this.events = [
      ...this.getTermEvents(this.terms),
      ...this.getPublicHolidayEvents(this.publicHolidays),
    ]
  }

  clickedDay(e: any) {
    const day: MonthViewDay             = e.day
    const publicHoliday: PublicHoliday  = this.getPublicHolidayFromEvents(day.events)
    const term: Term                    = this.getTermFromEvents(day.events)

    this.store.dispatch({ type: POPULATE_ACTIVE_DAY, payload: day })
    this.store.dispatch({ type: POPULATE_EDITABLE_TERM, payload: term })
    this.store.dispatch({ type: POPULATE_EDITABLE_PUBLIC_HOLIDAY, payload: publicHoliday })

    this.modalService.showModal(new ModalConfig(CalendarDayFormComponent))
  }

  // ////////////////////////
  // Day and event helpers.
  // ////////////////////////

  getPublicHolidayFromEvents(events: CalendarEvent[]): PublicHoliday {
    const event: CalendarEvent = events.find((e: CalendarEvent) => {
      return e.meta.type === "publicHoliday" })
    return event ? this.publicHolidays.find((ph => ph.id === event.meta.id)) : new PublicHoliday()
  }

  getTermFromEvents(events: CalendarEvent[]): Term {
    const event: CalendarEvent = events.find((e: CalendarEvent) => {
      return e.meta.type === "term" })
    return event ? this.terms.find((t => t.id === event.meta.id)) : new Term()
  }

  getTermEvents(terms: Term[]): CalendarEvent[] {
    const result = terms.map((t: Term): CalendarEvent => {
      return {
        title: t.name,
        color: { primary: "#a40930", secondary: "#f9d1db" },
        start: t.startDate,
        end: t.endDate,
        meta: { type: "term", id: t.id, term: t },
      }
    })

    terms.forEach((t: Term) => {
      t.extraDays.forEach((ed: TermExtraDay) => {
        result.push(
          {
            title: `For: ${ed.publicHoliday.name}`,
            color: { primary: "#a40930", secondary: "#f9d1db" },
            start: ed.date,
            allDay: true,
            meta: {
              type: "extraDay",
              id: t.id,
              term: t,
              publicHoliday: ed.publicHoliday
            },
          })
      })
    })

    return result
  }

  getTermEvent(day: MonthViewDay): CalendarEvent {
    return day.events.filter((e: CalendarEvent) => {
      return e.meta.type === "term" })[0]
  }

  getTermWeek(day: MonthViewDay): number {
    let result = 0

    const termEvent: CalendarEvent  = this.getTermEvent(day)
    const targetDate: Date          = new Date(day.date)
    const startDate: Date           = new Date(termEvent.start)
    const WEEK                      = 1000 * 60 * 60 * 24 * 7

    const startDateMs               = startDate.getTime()
    const endDateMs                 = targetDate.getTime()

    const delta = Math.abs(endDateMs - startDateMs)

    return Math.floor(delta / WEEK) + 1
  }

  getPublicHolidayEvents(publicHolidays: PublicHoliday[]): CalendarEvent[] {
    return publicHolidays.map((ph: PublicHoliday): CalendarEvent => {
      return {
        title: ph.name,
        color: { primary: "#1985d4", secondary: "#b8dbf5" },
        start: ph.date,
        allDay: true,
        meta: { type: "publicHoliday", id: ph.id },
      }
    })
  }

  getDate(date: Date): string {
    return date.getDate().toString()
  }

  getPublicHolidayNameFromDay(day: MonthViewDay): string {
    return day.events.find((e: CalendarEvent) => {
      return e.meta.type === "publicHoliday" }).title
  }

  getExtraDayNameFromDay(day: MonthViewDay): string {
    return day.events.find((e) => {
      return e.meta.type === "extraDay" }).title
  }

  isDayInTerm(day: MonthViewDay): boolean {
    const d = moment(day.date).day()

    const inTerm = day.events.filter((e) => {
      return e.meta.type === "term" }).length > 0

    const fallsOnHolidayTermAndWeekend = day
      .events
      .filter((e: CalendarEvent) => {
        return (e.meta.type === "term" &&
          e.meta.term.isHoliday &&
          (d === 0 || d === 6))
      })
      .length > 0

    const isExtraDay = day
      .events
      .filter((e) => e.meta.type === "extraDay")
      .length > 0


    let result: boolean = false

    if (inTerm) { result = true }
    if (inTerm && fallsOnHolidayTermAndWeekend) { result = false }
    if (isExtraDay) { result = true }

    return result
  }

  isExtraDay(day: MonthViewDay): boolean {
    return day.events.filter((e) => {
      return e.meta.type == "extraDay"
    }).length > 0
  }

  isDayToday(day: MonthViewDay): boolean {
    return day.date.getTime() === Utilities.makeDate(new Date()).getTime()
  }
  
  isDayMonday(day: MonthViewDay): boolean {
    return day.date.getDay() === 1
  }

  isDayPublicHoliday(day: MonthViewDay): boolean {
    return day.events.filter((e: CalendarEvent) => {
      return e.meta.type === "publicHoliday" }).length > 0
  }

  ngOnDestroy() {
    this.constantsSubscription.unsubscribe()
    this.calendarSubscription.unsubscribe()
  }

}



// WEBPACK FOOTER //
// /Users/jason/src/matrix/client/src/app/components/calendar-view.ts